/**
 * RuleSaveConfirm + RecalcNotice — the two pieces that make a rule change deliberate and
 * its consequence visible, shared by all three rule pages (Global / Department / Group).
 *
 * WHY THIS EXISTS
 * ---------------
 * An attendance rule decides how a day is SCORED — rest or working, on time or late, and
 * how much overtime. Change one and every daily summary already computed disagrees with
 * the configuration. Nothing recomputes them: `attendance_recalc_cron` re-runs only each
 * org's local yesterday + today, so a month that closed last week keeps its old numbers
 * indefinitely.
 *
 * That was invisible on every surface. On 2026-08-12 a live tenant's whole working week
 * was reclassified as rest and 30 punches came out as 15.3 recorded hours; the
 * configuration and the report had been disagreeing silently, and the only route back was
 * a manual recalculation nobody was told to run.
 *
 * So: confirm BEFORE saving (the admin sees what is about to change), and after saving
 * keep a persistent notice with a route to the calculation page. A transient snackbar is
 * wrong here — the action the user still has to take must not scroll away.
 *
 * One component, three call sites, on purpose. Three copies of this copy would drift, and
 * the field it guards is the one that spent a day having two notions of itself.
 */
import React from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { CalculateOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { ConfirmDialog } from '../../../components/primitives';
import { ruleFieldI18nKey } from './ruleSections';

/** Where an admin actually runs the recalculation. */
export const CALCULATION_PATH = '/attendance/calculation';

interface ConfirmProps {
  open: boolean;
  /** Scoring fields that differ from the loaded rule — from `changedScoringKeys`. */
  changedScoringKeys: string[];
  /** Human label for what this rule covers, e.g. a department or group name. */
  scopeLabel: string;
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

/**
 * Confirmation shown when Save is pressed. The recalculation sentence appears only when a
 * scoring field actually changed — a warning that fires on every save is one that gets
 * clicked through, and the capture-time gates (face, geofence, device) genuinely cannot
 * change how an existing punch is scored.
 */
export const RuleSaveConfirm: React.FC<ConfirmProps> = ({
  open, changedScoringKeys: changed, scopeLabel, onConfirm, onClose,
}) => {
  const { t } = useTranslation();
  const affectsScoring = changed.length > 0;

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      tone="primary"
      title={t('attendance:ruleSave.title')}
      description={affectsScoring
        ? t('attendance:ruleSave.descriptionScoring')
        : t('attendance:ruleSave.descriptionSettingsOnly')}
      subject={(
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{scopeLabel}</Typography>
          {affectsScoring && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('attendance:ruleSave.changedFields', {
                fields: changed
                  .map((key) => {
                    const i18nKey = ruleFieldI18nKey(key);
                    // Fall back to the raw key rather than rendering an empty name: a field
                    // added to SCORING_RULE_KEYS but not to RULE_SECTIONS should be visible,
                    // not silently dropped from the list of what is changing.
                    return i18nKey ? t(`attendance:ruleSections.fields.${i18nKey}.label`) : key;
                  })
                  .join(t('attendance:ruleSave.fieldSeparator')),
              })}
            </Typography>
          )}
        </Box>
      )}
      details={affectsScoring ? t('attendance:ruleSave.pastStaysUntilRecalculated') : undefined}
      confirmLabel={t('common:actions.save')}
      confirmLoadingLabel={t('common:status.saving')}
      cancelLabel={t('common:actions.cancel')}
    />
  );
};

interface NoticeProps {
  /** Scoring fields that were part of the save that just succeeded (rule pages). */
  changedScoringKeys?: string[];
  /**
   * What made a recalculation necessary. The rule pages derive it from the changed fields;
   * the holidays page passes `holiday` because the trigger there is the DATE (a holiday on
   * an already-scored day), not a field. One component so the sentence and the route to
   * the calculation page cannot drift between the two surfaces.
   */
  reason?: 'rule' | 'holiday';
  onDismiss: () => void;
}

/**
 * Persistent post-save notice. Stays until dismissed or the page is left, because it
 * carries an action the admin still has to take.
 */
export const RecalcNotice: React.FC<NoticeProps> = ({
  changedScoringKeys: changed = [], reason = 'rule', onDismiss,
}) => {
  const { t } = useTranslation();
  if (reason === 'rule' && changed.length === 0) return null;
  return (
    <Alert
      severity="warning"
      onClose={onDismiss}
      sx={{ width: '100%', mb: 2, borderRadius: 2 }}
      action={(
        <Button
          component={RouterLink}
          to={CALCULATION_PATH}
          size="small"
          color="inherit"
          startIcon={<CalculateOutlined fontSize="small" />}
          sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
        >
          {t('attendance:ruleSave.recalculateAction')}
        </Button>
      )}
    >
      <AlertTitle sx={{ fontWeight: 700, fontSize: '0.875rem', mb: 0.25 }}>
        {reason === 'holiday' ? t('attendance:ruleSave.savedTitleHoliday') : t('attendance:ruleSave.savedTitle')}
      </AlertTitle>
      {reason === 'holiday' ? t('attendance:ruleSave.savedBodyHoliday') : t('attendance:ruleSave.savedBody')}
    </Alert>
  );
};
